import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  Tooltip,
  partitionAvatarGroupItems,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useSession } from '../auth/SessionProvider';
import { featureNav } from '../config/featureNav';
import type { ActivePresenceDto } from '../api/types';

const REFRESH_MS = 30_000;

function ago(seconds: number): string {
  if (seconds < 60) return 'gerade aktiv';
  if (seconds < 3600) return `vor ${Math.floor(seconds / 60)} Min`;
  return `vor ${Math.floor(seconds / 3600)} Std`;
}

/** Status-Bubbles der gerade aktiven Nutzer (nur für Admins; nutzt /api/activity/active). */
export function ActiveUsersBubbles() {
  const api = useApi();
  const { me } = useSession();
  const [users, setUsers] = useState<ActivePresenceDto[]>([]);
  const [photos, setPhotos] = useState<Record<string, string>>({});
  const photosRef = useRef<Record<string, string>>({});

  const pageLabels = useMemo(() => {
    const map: Record<string, string> = { '/settings': 'Einstellungen' };
    for (const s of featureNav) for (const i of s.items) map[i.key] = i.text;
    return map;
  }, []);

  useEffect(() => {
    if (!me?.isAdmin) return;
    let active = true;
    const load = async () => {
      try {
        const u = await api.getActiveUsers(5);
        if (!active) return;
        setUsers(u);
        // Fotos je Nutzer einmalig nachladen (im Session-Cache halten).
        for (const user of u) {
          if (user.userObjectId in photosRef.current) continue;
          photosRef.current[user.userObjectId] = ''; // belegen, um Doppel-Fetch zu vermeiden
          api.getUserPhotoUrl(user.userObjectId).then((url) => {
            if (!active || !url) return;
            photosRef.current[user.userObjectId] = url;
            setPhotos({ ...photosRef.current });
          });
        }
      } catch {
        /* ignore */
      }
    };
    void load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(id);
      Object.values(photosRef.current).forEach((u) => u && URL.revokeObjectURL(u));
    };
  }, [api, me?.isAdmin]);

  if (!me?.isAdmin || users.length === 0) return null;

  const { inlineItems, overflowItems } = partitionAvatarGroupItems({
    items: users.map((u) => u.userObjectId),
    maxInlineItems: 4,
  });
  const byId = new Map(users.map((u) => [u.userObjectId, u]));
  const label = (u: ActivePresenceDto) =>
    `${u.displayName} — ${u.lastPath ? pageLabels[u.lastPath] ?? u.lastPath : '—'} · ${ago(u.secondsAgo)}`;

  return (
    <Tooltip relationship="label" content={`${users.length} Nutzer aktiv (letzte 5 Min)`}>
      <AvatarGroup size={28} layout="stack">
        {inlineItems.map((id) => {
          const u = byId.get(id)!;
          return (
            <Tooltip key={id} relationship="label" content={label(u)}>
              <AvatarGroupItem name={u.displayName} color="colorful" image={photos[id] ? { src: photos[id] } : undefined} />
            </Tooltip>
          );
        })}
        {overflowItems && (
          <AvatarGroupPopover>
            {overflowItems.map((id) => {
              const u = byId.get(id)!;
              return (
                <AvatarGroupItem key={id} name={u.displayName} color="colorful" image={photos[id] ? { src: photos[id] } : undefined} />
              );
            })}
          </AvatarGroupPopover>
        )}
      </AvatarGroup>
    </Tooltip>
  );
}
